import PropTypes from "prop-types";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useMediaQuery,
} from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { Close, Storefront, Refresh } from "@material-ui/icons";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import { AuthContext } from "../../context/Auth/AuthContext";
import {
  CustomerServiceDialog,
  ServiceRowActions,
} from "../../pages/Negocio/CustomerServiceDialog";
import { daysLeft } from "../../pages/Negocio/helpers";
import { useNegocioBridge, useNegocioLive } from "../../pages/Negocio/useNegocioSync";
const useStyles = makeStyles((theme) => ({
  title: { display: "flex", alignItems: "center", gap: 8, "& > div": { flex: 1, minWidth: 0 } },
  service: {
    padding: 12,
    marginBottom: 10,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 12,
    overflowWrap: "anywhere",
    "& .actionCluster": {
      display: "flex !important",
      flexWrap: "wrap",
      whiteSpace: "normal !important",
      gap: 4,
      marginTop: 8,
    },
    "& .actionCluster > .MuiIconButton-root": { width: 40, height: 40 },
  },
  serviceHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 8,
  },
  remaining: { flexShrink: 0, fontSize: 12, fontWeight: 600, textAlign: "right" },
}));
export const remainingTime = (sub) => {
  if (sub.status === "cancelled") return "Cancelado";
  const left = daysLeft(sub.endDate);
  if (left == null || !Number.isFinite(left)) return "Sin vencimiento";
  if (left < 0) return `Vencido hace ${Math.abs(left)} ${left === -1 ? "día" : "días"}`;
  if (left === 0) return "Vence hoy";
  return `${left} ${left === 1 ? "día restante" : "días restantes"}`;
};

export default function NegocioCustomerMenu({ ticket, menuOpen, open, onClose, onAvailable }) {
  const { user } = useContext(AuthContext);
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down("xs"));
  const classes = useStyles();
  const contactId = ticket.contact?.id || ticket.contactId;
  const allowed = !!(user?.super || user?.profile === "admin");
  const [customer, setCustomer] = useState(null);
  const [editor, setEditor] = useState(null);
  const [busy, setBusy] = useState(false);
  const generation = useRef(0);
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!open) return undefined;
    const timer = setInterval(() => setTick((value) => value + 1), 30000);
    return () => clearInterval(timer);
  }, [open]);
  useNegocioBridge();
  const load = useCallback(
    async (reportError = false) => {
      if (!allowed || !contactId) return;
      const current = ++generation.current;
      try {
        const { data } = await api.get(`/negocio/customers/${contactId}`);
        if (generation.current === current) setCustomer({ ...data, contactId });
      } catch (error) {
        if (generation.current !== current) return;
        setCustomer(null);
        if (reportError && ![403, 404].includes(error.response?.status)) toastError(error);
      }
    },
    [allowed, contactId]
  );
  useEffect(() => {
    generation.current += 1;
    setCustomer(null);
    onClose();
    setEditor(null);
    return () => {
      generation.current += 1;
    };
    // onClose is a parent state setter wrapper.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactId, ticket.id, allowed]);
  useEffect(() => {
    if (menuOpen) load(true);
  }, [menuOpen, load]);
  useNegocioLive(["customers", "subscriptions", "sales", "accounts"], () =>
    open || menuOpen ? load() : undefined
  );
  const notify = async ({ kind, channel, subscriptionId }) => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post("/negocio/notify", { notify: true, kind, channel, contactId, subscriptionId });
      toast.success("Datos enviados al cliente");
      await load();
    } catch (error) {
      toastError(error);
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => {
    onAvailable(customer);
  }, [customer, onAvailable]);
  if (!allowed || !customer) return null;
  const services = customer.subscriptions || [];
  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="sm"
        fullScreen={mobile}
        scroll="paper"
        aria-labelledby="ticket-negocio-title"
      >
        <DialogTitle disableTypography>
          <div className={classes.title}>
            <Storefront color="primary" />
            <div>
              <Typography variant="subtitle1" component="h2" id="ticket-negocio-title">
                Cliente de Negocio
              </Typography>
              <Typography variant="body2" noWrap>
                {customer.name || ticket.contact?.name}
              </Typography>
            </div>
            <IconButton aria-label="Actualizar servicios" onClick={() => load(true)}>
              <Refresh />
            </IconButton>
            <IconButton aria-label="Cerrar cliente de Negocio" onClick={onClose}>
              <Close />
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent dividers style={{ padding: 12 }}>
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 14 }}>
            {customer.number || ticket.contact?.number}
            {customer.customerType ? ` · ${customer.customerType}` : ""} · {services.length}{" "}
            servicios
          </Typography>
          {!services.length && (
            <Typography variant="body2">
              Este cliente todavía no tiene servicios registrados.
            </Typography>
          )}
          {services.map((sub) => (
            <div key={sub.id} className={classes.service}>
              <div className={classes.serviceHeader}>
                <Typography variant="subtitle2">
                  {sub.product?.name || "Servicio"}
                  {sub.product?.planLabel ? ` · ${sub.product.planLabel}` : ""}
                </Typography>
                <Typography
                  component="span"
                  className={classes.remaining}
                  color={
                    daysLeft(sub.endDate) != null && daysLeft(sub.endDate) <= 3
                      ? "error"
                      : "textSecondary"
                  }
                >
                  {remainingTime(sub)}
                </Typography>
              </div>
              <Typography variant="body2" color="textSecondary">
                {{
                  active: "Activo",
                  expired: "Vencido",
                  pending: "Pendiente",
                  cancelled: "Cancelado",
                }[sub.status] || sub.status}{" "}
                · {sub.endDate ? `Vence: ${String(sub.endDate).slice(0, 10)}` : "Sin vencimiento"}
              </Typography>
              {sub.account?.email && (
                <Typography variant="body2">
                  {sub.account.email}
                  {sub.profile?.name ? ` · ${sub.profile.name}` : ""}
                </Typography>
              )}
              <fieldset disabled={busy} style={{ border: 0, margin: 0, padding: 0, minWidth: 0 }}>
                <ServiceRowActions
                  sub={sub}
                  customer={customer}
                  onEdit={() => setEditor({ sub, mode: "edit" })}
                  onRenew={() => setEditor({ sub, mode: "renew" })}
                  onNotify={notify}
                  onDeleted={() => load(true)}
                  onChanged={() => load(true)}
                />
              </fieldset>
            </div>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Volver al ticket</Button>
        </DialogActions>
      </Dialog>
      <CustomerServiceDialog
        open={!!editor}
        mode={editor?.mode}
        sub={editor?.sub}
        customer={customer}
        onClose={() => setEditor(null)}
        onSaved={async () => {
          setEditor(null);
          await load(true);
        }}
      />
    </>
  );
}

NegocioCustomerMenu.propTypes = {
  ticket: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    contactId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    contact: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      name: PropTypes.string,
      number: PropTypes.string,
    }),
  }).isRequired,
  menuOpen: PropTypes.bool,
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onAvailable: PropTypes.func.isRequired,
};
